import type { NodeAvailability, NodeDef } from './types';
import { BALANCE } from '../data/balance';
import { NODES, NODE_BY_ID, ROOT_NODE_ID } from '../data/nodes';
import { abyssGateLevel, goldMultOf, nodeCost } from './economy';
import { DEFAULT_BALANCE, type ResolvedBalance } from './balance';

export type NodeLevels = Record<string, number>;

/** Arbre au départ d'une partie : seule la racine est possédée (niveau 1). */
export function initialNodes(): NodeLevels {
  const levels: NodeLevels = {};
  for (const n of NODES) levels[n.id] = n.id === ROOT_NODE_ID ? 1 : 0;
  return levels;
}

/**
 * **Arbre de campagne complet** (v0.53.0, A103) : tous les nœuds hors Abîme à
 * leur niveau maximum, l'Abîme à 0 sauf *Artisan des dés* à 1 (offert à
 * l'entrée en mode infini, A94-2). *Sans fond* (répétable, Abîme) reste à 0.
 * Sert au départ direct en mode infini (`core/endlessStart.ts`).
 */
export function fullCampaignTree(): NodeLevels {
  const levels: NodeLevels = {};
  for (const n of NODES) levels[n.id] = n.gate ? 0 : n.maxLevel;
  levels[ARTISAN_NODE_ID] = 1;
  return levels;
}

/** Niveau possédé d'un nœud (0 s'il est inconnu). */
export function nodeLevel(levels: NodeLevels, id: string): number {
  return levels[id] ?? 0;
}

/** Le nœud est-il débloqué (parent possédé) ? */
export function isUnlocked(levels: NodeLevels, def: NodeDef): boolean {
  if (!def.parent) return true;
  return nodeLevel(levels, def.parent) >= 1;
}

/**
 * Peut-on acheter un niveau de ce nœud ?
 * Raisons possibles : 'unknown' (id inexistant), 'locked' (parent manquant),
 * 'maxed' (niveau maximum atteint), 'gold' (or insuffisant).
 *
 * v0.46.0 (A94-3) : un nœud de l'**Abîme** n'a **pas** de condition de parent ;
 * sa raison propre est `'gate'` (porte du prochain niveau au-delà du meilleur
 * niveau atteint `bestLevel`, avec `gateLevel`). Son prix est `3 × Exp(porte)`
 * au multiplicateur d'or de l'arbre (`core/economy.ts : abyssNodeCost`).
 * *Sans fond* n'est jamais `'maxed'`. Les autres nœuds sont inchangés.
 */
export function canBuyNode(
  levels: NodeLevels,
  gold: number,
  id: string,
  bal: ResolvedBalance = DEFAULT_BALANCE,
  bestLevel = 0,
): NodeAvailability {
  const def = NODE_BY_ID[id];
  if (!def) return { ok: false, reason: 'unknown', cost: Infinity };
  const level = nodeLevel(levels, id);
  const cost = nodeCost(id, level, bal, { bestLevel, goldMult: goldMultOf(levels) });
  if (level >= def.maxLevel) return { ok: false, reason: 'maxed', cost };
  const gate = abyssGateLevel(def, level);
  if (gate !== null) {
    if (bestLevel < gate) return { ok: false, reason: 'gate', cost, gateLevel: gate };
  } else if (!isUnlocked(levels, def)) {
    const parent = def.parent ? NODE_BY_ID[def.parent] : null;
    return { ok: false, reason: 'locked', cost, parentName: parent?.name };
  }
  if (gold < cost) return { ok: false, reason: 'gold', cost };
  return { ok: true, cost };
}

/** Nœud racine de l'Abîme, offert à l'entrée en mode infini (A94-2). */
export const ARTISAN_NODE_ID = 'aby_artisan';

/**
 * Pose *Artisan des dés* au niveau 1 s'il vaut 0 (A94-2) — victoire sur le
 * Tricorne, partie déjà en mode infini au chargement. Renvoie `true` s'il
 * vient d'être accordé.
 */
export function grantArtisanLevel(levels: NodeLevels): boolean {
  if (nodeLevel(levels, ARTISAN_NODE_ID) >= 1) return false;
  levels[ARTISAN_NODE_ID] = 1;
  return true;
}

/**
 * Niveaux de nœuds de l'Abîme dont la porte s'ouvre quand le meilleur niveau
 * atteint passe de `from` à `to` (A94-3) : chaque niveau **non possédé** dont
 * la porte est dans `]from, to]`, 1-based (le niveau qui devient achetable).
 * *Sans fond* ne liste que son prochain niveau.
 */
export function abyssOpenings(
  levels: NodeLevels,
  from: number,
  to: number,
): { nodeId: string; level: number }[] {
  const out: { nodeId: string; level: number }[] = [];
  if (!(to > from)) return out;
  for (const def of NODES) {
    if (!def.gate) continue;
    const owned = nodeLevel(levels, def.id);
    const last = def.repeatable ? owned + 1 : def.maxLevel;
    for (let n = owned; n < last; n++) {
      const gate = abyssGateLevel(def, n);
      if (gate !== null && gate > from && gate <= to) out.push({ nodeId: def.id, level: n + 1 });
    }
  }
  return out;
}

/**
 * Palier d'un nœud = sa profondeur dans l'arbre (racine = 0).
 * Dérivé des `parent`, jamais saisi à la main : un nœud déplacé change de
 * palier tout seul (COMMENTS v0.3.0 §3.1). `Infinity` pour un id inconnu.
 */
export function nodeTier(id: string): number {
  let depth = 0;
  let def: NodeDef | undefined = NODE_BY_ID[id];
  if (!def) return Infinity;
  while (def.parent) {
    depth += 1;
    def = NODE_BY_ID[def.parent];
    if (!def || depth > NODES.length) return Infinity;
  }
  return depth;
}

export { NODES, NODE_BY_ID, ROOT_NODE_ID };

// ──────────────────────────────────── Info-bulles de niveau (COMMENTS §2)

/** Nombre à la française : virgule décimale, zéros de fin retirés. */
function fr(n: number, decimals = 2): string {
  const s = n.toFixed(decimals);
  return (decimals > 0 ? s.replace(/0+$/, '').replace(/[.,]$/, '') : s).replace('.', ',');
}

/** Pourcentage à la française, sans décimale inutile. */
function pct(n: number, decimals = 0): string {
  return `${fr(n * 100, decimals)} %`;
}

/**
 * Effet d'un nœud **à un niveau donné**, en une phrase.
 * `level === 0` décrit l'absence de bonus : c'est ce que l'info-bulle affiche
 * avant le premier achat.
 */
const NODE_EFFECT: Record<string, (level: number) => string> = {
  root: (l) => (l >= 1 ? 'Machine Gachapon de d4 débloquée.' : 'Machine Gachapon de d4 verrouillée.'),

  ars_dmg: (l) => (l > 0 ? `+${l} dégât de base du lancer par dé équipé.` : 'Aucun dégât de base supplémentaire.'),
  ars_heavy: (l) =>
    l > 0
      ? `Dégâts du lancer ×${fr(1 + BALANCE.nodeDamageMultPerLevel * l)}.`
      : 'Aucun multiplicateur de dégâts.',
  ars_fast: (l) =>
    l > 0
      ? `Temps de retour −${pct(BALANCE.nodeCooldownPerLevel * l)} (plancher ${fr(BALANCE.throwCooldownFloor, 1)} s).`
      : 'Temps de retour inchangé.',
  ars_crit: (l) =>
    l > 0 ? `+${pct(BALANCE.nodeCritPerLevel * l)} de chance de coup critique.` : 'Aucune chance de critique supplémentaire.',
  ars_critdmg: (l) =>
    l > 0 ? `+${pct(BALANCE.nodeCritDamagePerLevel * l)} de dégâts critiques additifs.` : 'Aucun bonus de dégâts critiques.',
  ars_goblet: (l) =>
    l > 0 ? `Gobelet débloqué : ${BALANCE.gobletDicePerLevel * l} dés Basiques.` : 'Aucun gobelet.',
  ars_hex: (l) =>
    l > 0 ? `${pct(BALANCE.nodeHexSharePerLevel * l)} des dés du contenant sont des d6.` : 'Contenant entièrement en d4.',
  ars_bowl: (l) =>
    l > 0
      ? `Bol débloqué : +${BALANCE.nodeBowlDicePerLevel * l} dés Basiques.`
      : 'Aucun bol.',
  ars_scope: (l) =>
    l > 0
      ? `+${pct(BALANCE.nodeScopeCritPerLevel * l)} de chance de coup critique.`
      : 'Aucune chance de critique supplémentaire.',
  ars_magiccrit: (l) =>
    l > 0
      ? `Dégâts critiques ×${fr(1 + BALANCE.nodeMagicCritPerLevel * l)}.`
      : 'Aucun multiplicateur de dégâts critiques.',
  ars_critmachine: (l) =>
    l > 0
      ? `Après un critique : temps de retour −${pct(BALANCE.nodeCritMachinePerLevel * l)} pendant ${fr(BALANCE.critMachineDuration, 0)} s.`
      : 'Un critique ne change pas le temps de retour.',
  ars_dodeca: (l) =>
    l > 0
      ? `${pct(BALANCE.nodeDodecaSharePerLevel * l)} des dés du contenant sont des d12.`
      : 'Aucun d12 dans le contenant.',
  ars_bucket: (l) =>
    l > 0
      ? `Seau débloqué : +${BALANCE.nodeBucketDicePerLevel * l} dés Basiques.`
      : 'Aucun seau.',
  // v0.26.0 — COMMENTS v0.25.0 §1.2 / A72.
  ars_dicemaster: (l) =>
    l >= 1
      ? 'Dégâts du lancer × la moyenne des raretés des dés Maîtres équipés (×1 à ×2).'
      : 'La rareté des dés équipés ne multiplie pas les dégâts.',
  ars_barrow: (l) =>
    l > 0
      ? `Brouette débloquée : +${fr(BALANCE.nodeBarrowDicePerLevel * l, 0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} dés Basiques.`
      : 'Aucune brouette.',
  ars_icosa: (l) =>
    l > 0
      ? `${pct(BALANCE.nodeIcosaSharePerLevel * l)} des dés du contenant sont des d20.`
      : 'Aucun d20 dans le contenant.',
  ars_critstreak: (l) =>
    l > 0
      ? `Dès le 2e critique consécutif : dégâts ×${fr(1 + BALANCE.nodeCritStreakPerLevel * l)} par critique consécutif, jusqu’à ${BALANCE.streakMaxStacks} cumuls.`
      : 'Les critiques consécutifs ne se cumulent pas.',
  ars_critadv: (l) =>
    l >= 1
      ? 'Critique tiré deux fois : le lancer est critique si l’un des tirages réussit.'
      : 'Un seul tirage de critique par lancer.',
  ars_cheat: (l) =>
    l >= 1
      ? 'Deux lancers à chaque lancer : celui au plus haut total de dégâts est appliqué.'
      : 'Un seul lancer à la fois.',

  shop_gold: (l) => (l > 0 ? `+${pct(BALANCE.nodeGoldPerLevel * l)} d’or à la mort d’un ennemi.` : 'Aucun or supplémentaire.'),
  shop_double: (l) =>
    l > 0
      ? `${pct(BALANCE.nodeGoldDoublePerLevel * l)} de chance de doubler l’or d’un ennemi tué.`
      : 'Aucune chance de doubler l’or.',
  shop_recycle: (l) => (l >= 1 ? 'Recycleur de dés débloqué.' : 'Recycleur verrouillé.'),
  shop_v6: (l) => (l >= 1 ? 'Machine Gachapon de d6 débloquée.' : 'Machine Gachapon de d6 verrouillée.'),
  shop_cheap: (l) =>
    l > 0 ? `−${pct(BALANCE.nodeGachaDiscountPerLevel * l)} sur le coût d’achat des dés.` : 'Prix des dés inchangé.',
  shop_rarity: (l) =>
    l > 0
      ? `+${pct(BALANCE.nodeRarityPerLevel * l)} de chances d’obtenir des dés Inhabituels ou plus.`
      : 'Chances de rareté inchangées.',

  shop_larcin: (l) =>
    l > 0
      ? `+${pct(BALANCE.nodeLarcinGoldPerLevel * l)} d’or à la mort d’un ennemi.`
      : 'Aucun or supplémentaire.',
  shop_v12: (l) => (l >= 1 ? 'Machine Gachapon de d12 débloquée.' : 'Machine Gachapon de d12 verrouillée.'),
  shop_twin: (l) =>
    l > 0
      ? `${pct(BALANCE.nodeTwinChancePerLevel * l)} de chance d’obtenir un second dé à chaque dé tiré.`
      : 'Un dé tiré reste un dé.',
  shop_tinker: (l) =>
    l > 0
      ? `+${pct(BALANCE.nodeRarityPerLevel * l)} de chances d’obtenir des dés Inhabituels ou plus (cumulé avec Modification mécanique).`
      : 'Chances de rareté inchangées.',
  shop_orsplosion: (l) =>
    l > 0
      ? `${pct(BALANCE.nodeOrsplosionPerLevel * l)} de chance de doubler l’or d’un ennemi tué.`
      : 'Aucune chance de doubler l’or.',
  shop_v20: (l) => (l >= 1 ? 'Machine Gachapon de d20 débloquée.' : 'Machine Gachapon de d20 verrouillée.'),
  shop_loyalty: (l) =>
    l > 0
      ? `−${pct(BALANCE.nodeGachaDiscountPerLevel * l)} sur le coût d’achat des dés (s’additionne avec Négociateur).`
      : 'Prix des dés inchangé.',
  shop_rigged: (l) =>
    l > 0
      ? `+${pct(BALANCE.nodeRarityPerLevel * l)} de chances d’obtenir des dés Inhabituels ou plus (cumulé avec Modification mécanique et Bidouilleur).`
      : 'Chances de rareté inchangées.',

  arc_day: (l) => (l > 0 ? `+${l} s de durée d’Expédition.` : 'Durée d’Expédition inchangée.'),
  arc_slot2: (l) => (l >= 1 ? '2e emplacement de dé Maître (sorts Paire et Yeux du serpent).' : 'Un seul emplacement de dé Maître.'),
  arc_power: (l) =>
    l > 0 ? `Effets des sorts ×${fr(1 + BALANCE.nodeSpellPowerPerLevel * l, 1)}.` : 'Effets des sorts inchangés.',
  arc_slot3: (l) => (l >= 1 ? '3e emplacement de dé Maître (sorts Brelan et 666).' : 'Pas de 3e emplacement.'),
  arc_keep: (l) =>
    l >= 1
      ? 'Le dé Maître de l’emplacement 1 est conservé après la mort.'
      : 'Tous les dés Maîtres sont perdus à la mort.',
  // v0.50.0 — A100-2 : même forme qu'*Arrière-garde*.
  arc_prep: (l) =>
    l > 0
      ? `Sorts de dé des dés Maîtres des emplacements 1 à ${BALANCE.nodePrepSlots} renforcés de ${pct(BALANCE.nodePrepPerLevel * l)} : leur bonus est multiplié par ${fr(1 + BALANCE.nodePrepPerLevel * l)}.`
      : 'Les sorts des dés Maîtres des emplacements 1 à 3 ne sont pas renforcés.',
  arc_slot4: (l) =>
    l >= 1 ? '4e emplacement de dé Maître (sorts Double paire et Carré).' : 'Pas de 4e emplacement.',
  arc_minion: (l) =>
    l >= 1
      ? `Larbin débloqué : il lance à votre place, temps de lancer ×${fr(BALANCE.minionCooldownFactor)} et dégâts ×${fr(BALANCE.minionDamageFactor)}.`
      : 'Aucun larbin.',
  arc_whip: (l) =>
    l > 0
      ? `Temps de lancer du Larbin −${pct(BALANCE.nodeWhipCooldownPerLevel * l)}.`
      : 'Temps de lancer du Larbin inchangé.',
  arc_keep2: (l) =>
    l >= 1
      ? 'Le dé Maître de l’emplacement 2 est conservé après la mort.'
      : 'Le dé Maître de l’emplacement 2 est perdu à la mort.',
  arc_sorcery: (l) =>
    l > 0
      ? `Effets des sorts ×${fr(1 + BALANCE.nodeSorceryPerLevel * l, 1)} (multiplicatif avec Puissance des sorts).`
      : 'Effets des sorts inchangés.',
  arc_slot5: (l) =>
    l >= 1 ? '5e emplacement de dé Maître (sorts Suite et Full).' : 'Pas de 5e emplacement.',
  arc_keep3: (l) =>
    l >= 1
      ? 'Le dé Maître de l’emplacement 3 est conservé après la mort.'
      : 'Le dé Maître de l’emplacement 3 est perdu à la mort.',
  arc_slavery: (l) =>
    l > 0
      ? `Dégâts du Larbin ×${fr(BALANCE.minionDamageFactor + BALANCE.nodeSlaveryPerLevel * l)} (au lieu de ×${fr(BALANCE.minionDamageFactor)}).`
      : `Dégâts du Larbin ×${fr(BALANCE.minionDamageFactor)}.`,
  arc_slot6: (l) => (l >= 1 ? '6e emplacement de dé Maître.' : 'Pas de 6e emplacement.'),
  arc_keep4: (l) =>
    l >= 1
      ? 'Le dé Maître de l’emplacement 4 est conservé après la mort.'
      : 'Le dé Maître de l’emplacement 4 est perdu à la mort.',
  arc_flux: (l) =>
    l > 0
      ? `Dès le 2e lancer consécutif à sort : dégâts ×${fr(1 + BALANCE.nodeSpellStreakPerLevel * l)} par lancer consécutif, jusqu’à ${BALANCE.streakMaxStacks} cumuls.`
      : 'Les lancers à sort consécutifs ne se cumulent pas.',
  // v0.43.0 — A89-6.
  arc_rearguard: (l) =>
    l > 0
      ? `Sorts de dégâts des deux derniers dés Maîtres renforcés de ${pct(BALANCE.nodeRearguardPerLevel * l)} : leur bonus est multiplié par ${fr(1 + BALANCE.nodeRearguardPerLevel * l)}.`
      : 'Les sorts des deux derniers dés Maîtres ne sont pas renforcés.',
  arc_slot7: (l) => (l >= 1 ? '7e emplacement de dé Maître.' : 'Pas de 7e emplacement.'),
  arc_keep5: (l) =>
    l >= 1
      ? 'Le dé Maître de l’emplacement 5 est conservé après la mort.'
      : 'Le dé Maître de l’emplacement 5 est perdu à la mort.',

  // v0.46.0 — Abîme (A94) ; effets de Matériau, Poker et Grimoires livrés en v0.47.0 (A95).
  aby_artisan: (l) =>
    l >= 1
      ? 'Abîme ouvert : Forge (Relance, Trempage, Trucage) et Corrupted GachaDice.'
      : 'Abîme, Forge et Corrupted GachaDice fermés.',
  aby_material: (l) =>
    l >= 1
      ? 'Bonus des Matériaux à 3 pièces.'
      : 'Aucun bonus de Matériau.',
  aby_poker: (l) =>
    l >= 1
      ? 'Triple paire, Double brelan, Grande suite, Maxi suite et Brelan carré.'
      : 'Aucun sort de Poker.',
  aby_grimoire_numbers: (l) =>
    l > 0
      ? `Sorts de dé +${pct(BALANCE.grimoirePerLevel * l)}${l >= BALANCE.grimoireWidenLevel ? ', conditions élargies' : ''}.`
      : 'Sorts de dé inchangés.',
  aby_enriched: (l) =>
    l >= 1
      ? `Corrupted GachaDice : ${pct(BALANCE.enrichedChance)} de dés Enrichis (affixes ×${fr(BALANCE.enrichedAffixFactor)}).`
      : 'Aucun dé Enrichi aux Corrupted GachaDice.',
  aby_hammer: (l) =>
    l > 0
      ? `Plafond de niveau des dés ${BALANCE.dieLevelMax + BALANCE.hammerDieLevelPerLevel * l} · recettes −${pct(BALANCE.hammerForgeDiscountPerLevel * l)}.`
      : `Plafond de niveau des dés ${BALANCE.dieLevelMax} · recettes au prix plein.`,
  aby_recipes: (l) =>
    l >= 1
      ? 'Forge : Fusion, Sublimation et Absolution ouvertes.'
      : 'Forge : Relance, Trempage et Trucage seulement.',
  aby_materiology: (l) =>
    l >= 1
      ? 'Caoutchouc, Silicium et Du Vide aux Corrupted GachaDice ; bonus à 5 pièces.'
      : 'Boisé, Osseux et Ferreux seulement.',
  aby_grimoire_faces: (l) =>
    l > 0
      ? `Sorts d’emplacement +${pct(BALANCE.grimoirePerLevel * l)}${l >= BALANCE.grimoireFourLevel ? ', Suites à trou, Carré dès trois dés' : l >= BALANCE.grimoireWidenLevel ? ', Suites à trou' : ''}.`
      : 'Sorts d’emplacement inchangés.',
  aby_bottomless: (l) =>
    l > 0 ? `Dégâts ×${fr(1 + BALANCE.bottomlessDamagePerLevel * l)}.` : 'Aucun bonus de dégâts.',
};

export interface NodeLevelText {
  /** Ce que le nœud apporte **au niveau possédé**. */
  current: string;
  /** Ce qu'apporterait le niveau suivant (ou « Niveau maximum atteint. »). */
  next: string;
}

/**
 * Textes d'info-bulle d'un nœud — COMMENTS v0.4.1 §2 : « les info-bulles des
 * talents indiquent désormais le bonus qu'ils donnent actuellement en plus de
 * l'effet de l'achat ».
 *
 * Fonction **pure**, en français, définie pour **tous** les nœuds. Un
 * identifiant inconnu renvoie deux tirets plutôt que de jeter.
 */
export function describeNodeLevel(id: string, level: number): NodeLevelText {
  const def = NODE_BY_ID[id];
  const describe = NODE_EFFECT[id];
  if (!def || !describe) return { current: '—', next: '—' };
  const current = Math.min(def.maxLevel, Math.max(0, Math.floor(level || 0)));
  return {
    current: describe(current),
    next: current >= def.maxLevel ? 'Niveau maximum atteint.' : describe(current + 1),
  };
}
